import { currencyLabel } from '@/helpers/currency';
import { Card, Tooltip, TrendChart } from '@payaca/ambrosia';

import { currencyCodeForRegion } from '@payaca/helpers/currencyHelper';
import { AccountRegions } from '@payaca/types/accountTypes';
import UntitledIcon from '@payaca/untitled-icons';
import { FC } from 'react';
import { useStatsChartOptions } from '../hooks/useStatsChartOptions';
import {
  CHART_HEIGHT,
  ChartSeriesConfig,
  RateSeriesConfig,
  StatConfig,
  TimeSeriesPoint,
} from '../utils';
import MiniSparkline from './MiniSparkline';

interface DashboardStatsCardProps {
  title: string;
  tooltip?: string;
  stats: StatConfig[];
  timeSeries: TimeSeriesPoint[];
  chartSeries: ChartSeriesConfig[];
  rateSeries?: RateSeriesConfig;
  region?: string;
  xAxisTitle?: string;
}

const DashboardStatsCard: FC<DashboardStatsCardProps> = ({
  title,
  tooltip,
  stats,
  timeSeries,
  chartSeries,
  rateSeries,
  region,
  xAxisTitle,
}) => {
  const baseCount = stats[0]?.data.count ?? 0;
  const currencyCode = currencyCodeForRegion[region as AccountRegions];

  const chartData = useStatsChartOptions({
    timeSeries,
    chartSeries,
    rateSeries,
    region,
  });

  return (
    <Card>
      <Card.Body className="p-0 @screen-md:p-5">
        {/* Header */}
        <div className="px-4 pt-4 @screen-md:px-0 @screen-md:pt-0">
          <h3 className="flex items-center gap-1.5">
            {title}
            {tooltip && (
              <Tooltip tooltipContent={tooltip} className="translate-y-0.5">
                <UntitledIcon
                  name="info-circle.3"
                  className="size-4 text-gray-400"
                />
              </Tooltip>
            )}
          </h3>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 p-4 pb-10 @screen-sm:grid-cols-3 @screen-md:px-0">
          {stats.map((stat) => (
            <div
              key={stat.key}
              className="flex items-center py-4 ps-1 @screen-sm:py-0"
            >
              <div className="shrink-0">
                <p className="flex items-center gap-1.5">
                  {stat.label}
                  {stat.tooltip && (
                    <Tooltip
                      tooltipContent={stat.tooltip}
                      className="translate-y-0.5"
                    >
                      <UntitledIcon
                        name="info-circle.3"
                        className="size-4 text-gray-400"
                      />
                    </Tooltip>
                  )}
                </p>
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  <h4>
                    {currencyLabel(stat.data.totalValue.value, currencyCode)}
                  </h4>
                  {stat.showRate && baseCount > 0 && (
                    <span
                      className="inline-flex items-center gap-x-1 supporting-body"
                      style={{ color: stat.rateColor ?? stat.color }}
                    >
                      {((stat.data.count / baseCount) * 100).toFixed(0)}%
                    </span>
                  )}
                </div>
                <p className="supporting-body text-gray-500">
                  {stat.data.count}
                </p>
              </div>
              <div className="flex flex-1 justify-center">
                {stat.data.sparklineValue && (
                  <MiniSparkline
                    data={stat.data.sparklineValue}
                    color={stat.color}
                    name={stat.label}
                    formatAsCurrency
                    region={region}
                  />
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Chart */}
        {chartData && (
          <div className="border-t border-gray-200 p-4 pt-10 pb-0 @screen-md:px-0">
            <TrendChart
              series={chartData.series}
              xAxisLabels={chartData.xAxisLabels}
              xAxisTitle={xAxisTitle}
              yAxes={chartData.yAxes}
              tooltipFormatter={chartData.tooltipFormatter}
              height={CHART_HEIGHT.TREND_CHART}
            />
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default DashboardStatsCard;
